import { Injectable } from "@angular/core";
import {
  IUserProfile,
  ME_USER,
} from "app/bundles/authorize/shared/user-profile";
import { UserProfileService } from "app/bundles/authorize/shared/user-profile.service";
import { Feature } from "app/bundles/requests/shared/models/feature";
import { IGeojsonRequest } from "app/bundles/requests/shared/models/geojsonRequest";
import { Service } from "app/bundles/requests/shared/models/service";
import { Status } from "app/bundles/requests/shared/models/status";
import { UtilService } from "app/bundles/requests/shared/services/util.service";
import * as _ from "lodash-es";
import { Observable, from as observableFrom, of as observableOf } from "rxjs";
import { concatMap, filter, toArray } from "rxjs/operators";

@Injectable()
export class FilterService {
  private userProfile: IUserProfile;

  constructor(
    private utilService: UtilService,
    private userProfileService: UserProfileService,
  ) {
    this.userProfileService.userProfile$.subscribe(
      (user) => (this.userProfile = user),
    );
  }

  findServices(geojsonData: IGeojsonRequest): Service[] {
    const services: Service[] = [];
    if (geojsonData.features && geojsonData.features.length > 0) {
      _.forEach(geojsonData.features, (value) => {
        const foundService = _.find(services, (s) => {
          return s.code === value.properties.service.code;
        });
        if (!foundService) {
          services.push(value.properties.service);
        }
      });
    }
    return services.sort((first, second) =>
      first.name.localeCompare(second.name),
    );
  }

  findStatuses(geojsonData: IGeojsonRequest): Status[] {
    const statuses: Status[] = [];
    if (geojsonData.features && geojsonData.features.length > 0) {
      _.forEach(geojsonData.features, (value) => {
        const foundService = _.find(statuses, (s) => {
          return s.code === value.properties.status.code;
        });
        if (!foundService) {
          statuses.push(value.properties.status);
        }
      });
    }
    return statuses.sort((first, second) =>
      first.name.localeCompare(second.name),
    );
  }

  findResponsibleUsers(geojsonData: IGeojsonRequest): string[] {
    let responsibleUsers = [];
    if (geojsonData.features && geojsonData.features.length > 0) {
      responsibleUsers = Array.from(
        new Set(
          geojsonData.features.map((request) => {
            if (
              request.properties.responsible.userName &&
              request.properties.responsible.userCode ===
                this.userProfile.userName.toUpperCase()
            ) {
              return `${request.properties.responsible.userName} ${ME_USER}`;
            } else {
              return request.properties.responsible.userName;
            }
          }),
        ),
      );
    }
    responsibleUsers.sort((first, second) => first.localeCompare(second));
    const foundIndex = responsibleUsers.findIndex(
      (user) => user && user.includes(ME_USER),
    );
    if (foundIndex > -1) {
      const responsibleMe = responsibleUsers[foundIndex];
      responsibleUsers.splice(foundIndex, 1);
      responsibleUsers.unshift(responsibleMe);
    }
    return responsibleUsers;
  }

  findResponsibleGroups(geojsonData: IGeojsonRequest): string[] {
    let responsibleGroups = [];
    if (geojsonData.features && geojsonData.features.length > 0) {
      responsibleGroups = Array.from(
        new Set(
          geojsonData.features.map(
            (request) => request.properties.responsible.groupName,
          ),
        ),
      );
    }
    return responsibleGroups.sort((first, second) =>
      first.localeCompare(second),
    );
  }

  filterGeojsonData(geojsonData: IGeojsonRequest, filterParameter: any): any {
    const foundFeatures: Feature[] = _.filter(geojsonData.features, (o) => {
      return o.properties.service.code === filterParameter.serviceCode;
    });
    return this.utilService.convertFeatureToGeojson(foundFeatures);
  }

  searchRequests(term: string, geojsonData: IGeojsonRequest): any {
    let features: any[] = [];
    if (geojsonData.features && geojsonData.features.length > 0) {
      features = _.filter(geojsonData.features, function (o) {
        return (
          o.properties.requestNumber.includes(term) ||
          o.properties.formattedAddress.includes(term)
        );
      });
    }
    return this.utilService.convertFeatureToGeojson(features);
  }

  filter(geojsonData: IGeojsonRequest, parameters: any): Observable<any> {
    const features: any[] = geojsonData.features;
    return observableOf(features).pipe(
      concatMap((array) => observableFrom(array)),
      filter((feature) => {
        return (
          (parameters.serviceCode === "" ||
            feature.properties.service.code === parameters.serviceCode) &&
          (parameters.searchTerm === "" ||
            feature.properties.requestNumber.includes(parameters.searchTerm))
        );
      }),
      toArray(),
    );
  }

  differenceAlerts(
    startGeojsonData: IGeojsonRequest,
    endGoejsonData: IGeojsonRequest,
  ): Feature[] {
    const startFeatures: Feature[] =
      startGeojsonData && startGeojsonData.features
        ? startGeojsonData.features
        : [];
    const endFeatures: Feature[] =
      endGoejsonData && endGoejsonData.features ? endGoejsonData.features : [];

    const results = _.differenceWith(endFeatures, startFeatures, (e, s) => {
      return e.properties.id === s.properties.id;
    });

    return results;
  }
}
